# FoosMonster FAQ

## General

### What is FoosMonster?

A foosball tournament manager that runs in your phone's browser. No app to install. It handles player registration, matchmaking, scorekeeping, skill ratings, and the leaderboard -- all in real time.

### How do I join a tournament?

The tournament creator shares a link, usually via a QR code you scan with your phone. When you open the link, you're automatically added as a player. You'll be asked to enter your name and choose whether you want to play singles and Ironman matches.

### Can I opt out of singles (Ironman) matches?

Yes. When you first join, you're asked "Would you like to play singles if a table is available?" If you choose NO, you'll never be put into a 1v1 or 1v1v1 match. You're doubles-only.

### How do I score a match?

The creator taps the active match card. A score entry screen appears.

- **Doubles or singles (2 teams)**: a score buttons appears for each team. Tap the **loser's** score (0 through 6). The winner is assumed to have scored 7. So if the score was 7-4, you tap 4 on the losing team's score buttons.
- **Ironman (3 teams)**: tap the **winner's** button. Everyone else is a loser.

---

## For Tournament Creators

### How do I mark players as present?

Tap the green checkmark icon next to the player's name on the PLAYERS tab. The icon turns green when they're present. Only present players are considered when generating matchups.

### How do I cancel a match?

Tap the trash icon on the active match card. The match is deleted and those players become available for the next round.

### How do I change the number of tables?

Go to the SETTINGS tab and use the plus/minus buttons to set how many tables are available. The matchmaker uses this number to calculate how many spots are free.

### What is the "Equalize game counts" switch?

The matchmaker naturally tries to keep game counts even throughout the tournament -- in fact, Step 4 of the algorithm always picks players who have played the fewest games first. So you usually don't need this switch at all.

The Equalize switch is a tool for the very end of the tournament, when you want to force exact parity before a final round. When you flip it on, the system looks at the player who has played the most games and sets that number as a hard cap. Anyone who has reached that cap sits out the next round, giving the others a chance to catch up exactly. Once everyone is even, a winner can be declared.

---

## How Matchups Are Made

This is the core of FoosMonster. Every time the tournament creator hits "NEXT ROUND," the system runs through a multi-step process to decide who plays who. Here's exactly how it works.

### Step 1: Who's eligible?

Only players who are both **present** and **not currently in a match** are considered. If the "Equalize game counts" switch is on, players who have hit the game cap are also excluded.

### Step 2: Order everyone by skill

Every player has a **skill rating** that the system tracks. It's the number shown in parentheses next to your name on the PLAYERS tab. The system lines everyone up weakest to strongest.

### Step 3: How many spots are open?

```
available spots = (total tables - tables with active matches) × 4
```

Each free table provides 4 spots (two teams of two). There's no round-based queuing -- the system uses whatever tables are free right now.

### Step 4: Pick the players who have played the fewest games

From the eligible players, the system picks the ones who have played the **least** so far, up to the number of available spots. If there's a tie (same number of games played), the weaker-rated player gets picked first. This gives everyone roughly the same number of games over the course of the tournament.

### Step 5: Combine skill rating with tournament performance

Once the players are chosen, they're sorted by a combined score: their OpenSkill rating is taken as a starting position, then their win/loss record for this tournament shifts them up or down. A player who has been winning all night gets moved up the order as if they're stronger than their rating says. A player on a cold streak gets moved down.

This combined ordering then determines who plays who. Players close to each other in the final order will end up on the same table. The result is that you tend to play against people at a similar skill level, adjusted for who's hot and who's not tonight.

### Step 6: Decide who plays singles

Normally you want groups of 4 for doubles. If the number of selected players doesn't divide evenly by 4 (say you have 6 players, leaving a remainder of 2), some players need to play singles.

The system looks at who's willing to play singles and picks the ones who have played singles the **fewest times** so far. This ensures no one gets stuck playing solo every round. If the remainder can't be filled by willing players, no singles match is created and those players sit out.

### Step 7: Form doubles teams (strongest + weakest)

Players are processed in groups of 4, strongest to weakest. Within each group of 4:

- **Team 1**: strongest player + weakest player
- **Team 2**: second-strongest + second-weakest

This is the "strongest with weakest" approach. It creates balanced matches where stronger players can help carry the weaker ones, making games more competitive and fun for everyone.

Each group of 4 gets its own table, numbered 1, 2, 3, etc.

### Step 8: Form singles matches

Any remaining players who are willing to play singles form a 1v1 or 1v1v1 match. Each person plays for themselves -- no partners.

### What about edge cases?

- **0 or 1 eligible players**: nothing happens, try again after more players arrive or matches finish
- **2 players**: they play a singles match on one table
- **3 players**: they play a 1v1v1 Ironman match on one table
- **All tables busy**: the NEXT ROUND button is disabled until a match is scored
- **All players at the game cap**: nothing happens until the cap is raised or Equalize mode is turned off

---

## Points, Scoring, and the Leaderboard

### How many points do I get for winning?

| Result | Points |
|---|---|
| Win | +3 |
| Loss by a close score (6-7) | +1 |
| Loss by any other score | 0 |

These are your **tournament points** -- the number that determines your rank on the leaderboard. They are separate from your skill rating (see OpenSkill section below).

### How are goals tracked?

- The winning team is recorded as scoring 7 goals.
- The losing team is recorded at their actual score (0 through 6).
- Ironman goals are not tracked.

### How does the leaderboard work?

Players are sorted by tournament points, highest first. If two players have the same number of points, they share the same place (both are #1, for example). The next player after them would be #3.

Players who are not present appear at the bottom of the list with no place number. They still have their points and rating intact, but they don't take up a rank.

---

## OpenSkill Ratings (The Number in Parentheses)

### What is OpenSkill?

OpenSkill is the rating system FoosMonster uses. It's similar to a chess rating or TrueSkill (used in Xbox matchmaking), but it's specifically designed for team games where the number of players per team can vary.

Every player has a rating that the system tracks. You see it in parentheses next to your name on the PLAYERS tab, like "(431)". It looks like a traditional chess rating, so the higher the number, the better the player.

### What is the number in parentheses next to my name?

That is your **display rating**. It starts at 200 for a brand new player. When you win, it goes up. When you lose, it goes down. The more you play, the more accurate it becomes.

This is a **conservative** estimate of your skill. The system intentionally under-promises a little, so your rating really means "at least this good."

### How much does my rating change when I win or lose?

It depends on two things:

1. **How certain the system is about you** (more on that below)
2. **Who you beat or lost to**

If you beat a player with a much higher rating, your rating jumps significantly. If you lose to someone much weaker, your rating drops significantly. If you beat someone at your own level, the change is modest.

The system also considers how many players were on each team. A win in doubles counts the same for both players, and the opposing team's combined skill is what matters.

### Why does my rating change more when I'm new?

When you start, the system has no data on you. Your display rating starts at 200, but the system is very uncertain about that estimate. Every game provides a lot of new information, so your rating can swing dramatically at first.

As you play more games, the system becomes more confident. Your rating changes more slowly because each new result is just one data point among many.

That confidence number never stops shrinking entirely -- a small amount of uncertainty is added back before each calculation so ratings can always change -- but the effect is strongest in your first few games.

### What does a rating of 200 mean?

It's the starting point for everyone. It doesn't mean "zero skill" -- it means you haven't played enough games for the system to know where you stand. As you play, your rating will rise or fall from there. And don't worry, your rating can never drop below 50, even on a rough losing streak.

### How does partner skill affect things?

When you play doubles, your team's strength is the **combination** of both players. If you and a very strong partner beat two average players, your rating won't jump much because the system expected you to win. If you and a weak partner beat two strong players, that's a surprise -- both you and your partner get a big boost.

This means you should never worry about "carrying" a weaker partner. The system accounts for the full team against the full opposing team.

### How is OpenSkill different from tournament points?

- **Tournament points** (the 0, 1, 3 on the leaderboard) reset to zero for each new tournament. They determine who wins this tournament.
- **Skill rating** (the number in parentheses) is global and persistent. It follows you across tournaments. It's used for matchmaking balance, not for determining tournament winners.

A player with a low tournament score might have a high skill rating (they're good but have had bad luck this event). A tournament leader might have a low skill rating (they're having a great day but aren't historically strong).
